import React, { useState, useEffect } from 'react';
import { customerService } from '../services/customerService';
import CustomerModal from './CustomerModal';

const ContactModal = ({ contact, onSave, onClose, defaultCustomerId }) => {
  const [formData, setFormData] = useState({
    customer_id: defaultCustomerId ? String(defaultCustomerId) : '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: '',
    billing_contact: false,
  });
  const [customers, setCustomers] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);

  const isEditing = !!contact;

  useEffect(() => {
    customerService.getCustomers().then(res => setCustomers((res.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
  }, []);

  useEffect(() => {
    if (contact) {
      setFormData({
        customer_id: contact.customer_id || '',
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        position: contact.position || '',
        billing_contact: contact.billing_contact || false,
      });
    }
  }, [contact]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      await onSave(formData);
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: error.response?.data?.message || 'An error occurred. Please try again.' });
      }
    }

    setLoading(false);
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-300 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
      errors[field]
        ? 'border-red-500 focus:border-red-500'
        : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400'
    }`;

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {isEditing ? 'Edit Customer Contact' : 'Add New Customer Contact'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl font-semibold leading-none transition-colors"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {errors.general && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              {errors.general}
            </div>
          )}

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">
              Customer <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <select
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
                disabled={!!defaultCustomerId && !contact}
                className={inputClass('customer_id') + ' flex-1'}
              >
                <option value="">Select a customer...</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowNewCustomerModal(true)}
                className="px-3 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 border-2 border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors whitespace-nowrap"
              >+ New</button>
            </div>
            {errors.customer_id && <p className="text-red-500 dark:text-red-400 text-sm">{errors.customer_id[0]}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="First name"
                className={inputClass('first_name')}
              />
              {errors.first_name && <p className="text-red-500 dark:text-red-400 text-sm">{errors.first_name[0]}</p>}
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Last name"
                className={inputClass('last_name')}
              />
              {errors.last_name && <p className="text-red-500 dark:text-red-400 text-sm">{errors.last_name[0]}</p>}
            </div>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">
              Position
            </label>
            <input
              type="text"
              name="position"
              value={formData.position}
              onChange={handleChange}
              placeholder="Job title / position"
              className={inputClass('position')}
            />
            {errors.position && <p className="text-red-500 dark:text-red-400 text-sm">{errors.position[0]}</p>}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email address"
              className={inputClass('email')}
            />
            {errors.email && <p className="text-red-500 dark:text-red-400 text-sm">{errors.email[0]}</p>}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">
              Phone
            </label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Phone number"
              className={inputClass('phone')}
            />
            {errors.phone && <p className="text-red-500 dark:text-red-400 text-sm">{errors.phone[0]}</p>}
          </div>

          <div className="flex items-center gap-3 py-1">
            <input
              type="checkbox"
              id="billing_contact"
              name="billing_contact"
              checked={formData.billing_contact}
              onChange={handleChange}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500 cursor-pointer"
            />
            <label htmlFor="billing_contact" className="text-sm font-semibold text-gray-900 dark:text-white cursor-pointer">
              Billing Contact
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Contact')}
            </button>
          </div>
        </form>
      </div>
    </div>

    {showNewCustomerModal && (
      <CustomerModal
        onSave={async (data) => {
          const res = await customerService.createCustomer(data);
          const newCustomer = res.data;
          const updated = await customerService.getCustomers();
          setCustomers((updated.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)));
          setFormData(prev => ({ ...prev, customer_id: String(newCustomer.id) }));
          setShowNewCustomerModal(false);
        }}
        onClose={() => setShowNewCustomerModal(false)}
      />
    )}
    </>
  );
};

export default ContactModal;
